import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchData, postData, putData } from "../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  ThinDropdownWithSearch,
  ThinTextField,
  CustomConfirmModal,
} from "../common/GeneralComponent";
import Select from "react-select";
import DataTable from "react-data-table-component";
import PermissionButton from "../common/PermissionButton";

const ManageBOQs = () => {
  const { villageId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];
  const [activeTab, setActiveTab] = useState("Location");
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [ucs, setUcs] = useState([]);
  const [ucName, setUcName] = useState("");
  const [dehs, setDehs] = useState([]);
  const [villages, setVillages] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTehsil, setSelectedTehsil] = useState(null);
  const [selectedUc, setSelectedUc] = useState(null);
  const [selectedDeh, setSelectedDeh] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [masterPlanFile, setMasterPlanFile] = useState(null);
  const [boqs, setBoqs] = useState([]);
  const [features, setFeatures] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedSubType, setSelectedSubType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [constructionType, setConstructionType] = useState("");
  const [cost, setCost] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [boqFile, setBoqFile] = useState(null);
  const boqFileInputRef = useRef(null);
  const clearBoqFileInput = () => {
    setBoqFile(null);
    if (boqFileInputRef.current) boqFileInputRef.current.value = "";
  };
  const [defaultBoqFile, setDefaultBoqFile] = useState(null);
  const [details, setDetails] = useState("");
  const [households, setHouseholds] = useState([]);
  const [selectedHouseholds, setSelectedHouseholds] = useState([]);
  const [editingBoqId, setEditingBoqId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [locationErrors, setLocationErrors] = useState({});
  const [proposedCode, setProposedCode] = useState("");
  const [selectedVillageData, setSelectedVillageData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalLocationData, setOriginalLocationData] = useState(null);
  const [canAccessBoqsTab, setCanAccessBoqsTab] = useState(false);
  const [selectedMasterPlanFileName, setSelectedMasterPlanFileName] =
    useState("");

  const constructionOptions = [
    { label: "Rehab", value: "rehab" },
    { label: "New", value: "new" },
  ];

  const getFileUrl = (filePath) => {
    if (!filePath) return "";
    const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8000";
    if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
      return filePath;
    }
    if (filePath.startsWith("/uploads")) {
      return `${baseUrl}${filePath}`;
    }
    return `${baseUrl}/uploads/${filePath}`;
  };

  const validateFile = (file, type) => {
    if (!file) return { isValid: false, error: "No file selected" };
    // const maxSize = 1 * 1024 * 1024;
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return { isValid: false, error: "File size must be less than 1MB" };
    }
    if (type === "pdf") {
      if (file.type !== "application/pdf") {
        return { isValid: false, error: "Only PDF files are allowed" };
      }
    } else if (type === "image") {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        return {
          isValid: false,
          error: "Only JPEG, PNG, and PDF files are allowed",
        };
      }
    }
    return { isValid: true, error: null };
  };

  const openPdfInNewTab = (filePath, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (filePath) {
      const fullUrl = getFileUrl(filePath);
      window.open(fullUrl, "_blank", "noopener,noreferrer");
    }
  };

  const fetchDistricts = async () => {
    try {
      const res = await fetchData("districts", "GET");
      setDistricts(
        res.data.map((d) => ({ label: d.district_name, value: d.district_id }))
      );
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  const fetchTehsils = async (districtId) => {
    try {
      const res = await fetchData(`tehsils?district_id=${districtId}`, "GET");
      setTehsils(
        res.data.map((t) => ({ label: t.tehsil_name, value: t.tehsil_id }))
      );
    } catch (error) {
      console.error("Error fetching tehsils:", error);
    }
  };

  const fetchUcs = async (tehsilId) => {
    try {
      const res = await fetchData(`ucs?tehsil_id=${tehsilId}`, "GET");
      setUcs(res.data.map((u) => ({ label: u.uc_name, value: u.uc_id })));
    } catch (error) {
      console.error("Error fetching UCs:", error);
    }
  };

  const fetchDehs = async (tehsilId) => {
    try {
      const res = await fetchData(`dehs?tehsil_id=${tehsilId}`, "GET");
      setDehs(res.data.map((d) => ({ label: d.deh_name, value: d.deh_id })));
    } catch (error) {
      setDehs([]);
      console.error("Error fetching dehs:", error);
    }
  };

  const fetchVillages = async (dehId) => {
    try {
      const res = await fetchData(`villages?deh_id=${dehId}`, "GET");
      setVillages(
        res.data.map((v) => ({ label: v.village_name, value: v.village_id }))
      );
    } catch (error) {
      console.error("Error fetching villages:", error);
    }
  };

  const fetchVillageDetails = async (villageId) => {
    try {
      const res = await fetchData(`villages?village_id=${villageId}`, "GET");
      if (res.data.length) {
        setSelectedVillageData(res.data[0]);
      }
    } catch (error) {
      console.error("Error fetching village details:", error);
    }
  };

  const loadLocationHierarchy = async (villageId) => {
    try {
      const res = await fetchData(`villages?village_id=${villageId}`, "GET");
      if (res.data.length) {
        const village = res.data[0];
        const locationData = {
          district_id: village.district_id,
          tehsil_id: village.tehsil_id,
          uc_id: village.uc_id,
          deh_id: village.deh_id,
          village_id: village.village_id,
        };
        setOriginalLocationData(locationData);
        await fetchDistricts();
        setSelectedDistrict(village.district_id);
        await fetchTehsils(village.district_id);
        setSelectedTehsil(village.tehsil_id);
        await fetchUcs(village.tehsil_id);
        setSelectedUc(village.uc_id);
        // await fetchDehs(village.uc_id);
        await fetchDehs(village.tehsil_id);
        setSelectedDeh(village.deh_id);
        await fetchVillages(village.deh_id);
        setSelectedVillage(village.village_id);
        setSelectedVillageData(village);

        await fetchBoqs(village.village_id);

        return locationData;
      }
    } catch (error) {
      console.error("Error loading location hierarchy:", error);
      toast.error("Failed to load location data");
      return null;
    }
  };

  const fetchFeatures = async () => {
    try {
      const res = await fetchData("features", "GET");
      setFeatures(
        res.data.map((f) => ({ label: f.feature_name, value: f.feature_id }))
      );
    } catch (error) {
      console.error("Error fetching features:", error);
      toast.error("Failed to fetch features");
    }
  };

  const fetchSubTypes = async (featureId) => {
    try {
      const res = await fetchData(
        `feature-subtypes-by-feature/${featureId}`,
        "GET"
      );
      setSubTypes(
        res.data.map((s) => ({
          label: s.feature_sub_type,
          value: s.feature_sub_type_id,
        }))
      );
    } catch (error) {
      console.error("Error fetching subtypes:", error);
      toast.error("Failed to fetch subtypes");
    }
  };

  const fetchCategories = async (subTypeId) => {
    try {
      const res = await fetchData(
        `feature-categories-fetch-by-subtype/${subTypeId}`,
        "GET"
      );
      setCategories(
        res.data.map((c) => ({
          label: c.category_name,
          value: c.feature_category_id,
        }))
      );
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    }
  };

  const fetchDesigns = async (categoryId) => {
    try {
      const res = await fetchData(`feature-designs/${categoryId}`, "GET");

      res.data.forEach((item, index) => {});

      const designsData = res.data.map((d) => ({
        label: d.design_name,
        value: d.feature_category_designs_id,
        boq_file: d.boq_file,
      }));

      setDesigns(designsData);
    } catch (error) {
      console.error("Error fetching designs:", error);
      toast.error("Failed to fetch designs");
    }
  };

  const fetchHouseholds = async (villageId) => {
    try {
      const res = await fetchData(
        `householdinformation`,
        "GET",
        null,
        {},
        {
          village_id: villageId,
          verified_from_dqa: 1,
          status: 1,
          limit: 1000,
        }
      );

      if (
        res.data &&
        res.data.household_information &&
        res.data.household_information.length > 0
      ) {
        setHouseholds(
          res.data.household_information.map((hh) => ({
            label: `${hh.household_number} - ${hh.hh_head_name}`,
            value: hh.household_information_id,
          }))
        );
      } else {
        setHouseholds([]);
      }
    } catch (error) {
      console.error("Error fetching households:", error);
      toast.error("Failed to fetch households for this village");
      setHouseholds([]);
    }
  };

  const fetchBoqs = async (villageId) => {
    try {
      const res = await fetchData(`proposed-features/boqs/${villageId}`, "GET");

      // If API returns an empty array, handle it gracefully
      if (!res.data || res.data.length === 0) {
        setBoqs([]);
        setUcName("");
        return; // ✅ stop here — no error toast
      }

      // ✅ When data is present
      setBoqs(res.data);
      setUcName(res.data[0]?.uc_name || "");
    } catch (error) {
      console.error("Error fetching BOQs:", error);
      // ✅ show toast only for actual errors, not "no record" cases
      if (!error.message?.includes("delete")) {
        toast.error("Failed to fetch BOQs");
      }
      setBoqs([]);
    }
  };

  const handleMasterPlanFileChange = (e) => {
    const file = e.target.files[0];
    setMasterPlanFile(file);
    setSelectedMasterPlanFileName(file ? file.name : "");
    setLocationErrors((prev) => ({ ...prev, masterPlanFile: "" }));
  };

  const validateLocationForm = () => {
    const newErrors = {};
    if (!selectedDistrict) newErrors.district = "District is required";
    if (!selectedTehsil) newErrors.tehsil = "Tehsil is required";
    // if (!selectedUc) newErrors.uc = "UC is required";
    if (!selectedDeh) newErrors.deh = "Deh is required";
    if (!selectedVillage) newErrors.village = "Village is required";

    // master plan file validation
    if (
      !isEditMode &&
      !masterPlanFile &&
      !selectedVillageData?.master_plan_file
    ) {
      newErrors.masterPlanFile = "Master Plan File is required";
    }

    setLocationErrors(newErrors);

    // canAccessBoqsTab based on validation
    const isValid = Object.keys(newErrors).length === 0;
    setCanAccessBoqsTab(isValid);

    return isValid;
  };
  const isValidLatitude = (value) => {
    return /^-?([0-8]?\d(\.\d+)?|90(\.0+)?)$/.test(value);
  };
  const isValidLongitude = (value) => {
    return /^-?((1?[0-7]?\d(\.\d+)?)|180(\.0+)?)$/.test(value);
  };

  const validateBoqForm = () => {
    const newErrors = {};
    if (!selectedFeature) newErrors.feature = "Feature is required";
    if (!selectedSubType) newErrors.subType = "Sub Type is required";
    if (!selectedCategory) newErrors.category = "Category is required";
    if (!selectedDesign) newErrors.design = "Design is required";
    if (!constructionType)
      newErrors.constructionType = "Construction Type is required";
    if (!cost || isNaN(cost)) newErrors.cost = "Cost (PKR) is required";

    if (!latitude || latitude.trim() === "") {
      newErrors.latitude = "Latitude is required";
    } else if (!isValidLatitude(latitude)) {
      newErrors.latitude =
        "Latitude must be between -90 and 90 with valid format";
    }

    if (!longitude || longitude.trim() === "") {
      newErrors.longitude = "Longitude is required";
    } else if (!isValidLongitude(longitude)) {
      newErrors.longitude =
        "Longitude must be between -180 and 180 with valid format";
    }

    if (selectedHouseholds.length === 0)
      newErrors.households = "At least one household must be selected";

    const hasExistingBoqFile =
      editingBoqId &&
      boqs.find((b) => b.boqs_proposed_id === editingBoqId)?.boq_file_path;
    if (!defaultBoqFile && !boqFile && !hasExistingBoqFile && !editingBoqId) {
      newErrors.boqFile = "BOQ document is required";
    }

    if (!details.trim()) newErrors.details = "Details are required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateLocationForm()) {
      toast.error("Please fill all required location fields");
      return;
    }
    setActiveTab("BOQs");
    fetchHouseholds(selectedVillage);
    fetchBoqs(selectedVillage);
  };

  const handleSubmitBoq = async () => {
    if (!validateBoqForm()) {
      return;
    }
    if (boqFile) {
      const validation = validateFile(boqFile, "pdf");
      if (!validation.isValid) {
        toast.error(validation.error);
        return;
      }
    }
    if (masterPlanFile) {
      const validation = validateFile(masterPlanFile, "image");
      if (!validation.isValid) {
        toast.error(validation.error);
        return;
      }
    }
    const formData = new FormData();
    const locationData = originalLocationData || {
      district_id: selectedDistrict,
      tehsil_id: selectedTehsil,
      uc_id: selectedUc,
      deh_id: selectedDeh,
      village_id: selectedVillage,
    };
    formData.append("district_id", locationData.district_id);
    formData.append("tehsil_id", locationData.tehsil_id);
    formData.append("uc_id", locationData.uc_id);
    formData.append("uc_name", ucName);
    formData.append("deh_id", locationData.deh_id);
    formData.append("village_id", locationData.village_id);
    formData.append("feature_id", selectedFeature);
    formData.append("feature_sub_type_id", selectedSubType);
    formData.append("feature_category_id", selectedCategory);
    formData.append("feature_category_designs_id", selectedDesign);
    formData.append("construction_type", constructionType);
    formData.append("cost", cost);
    formData.append("latitude", latitude || "");
    formData.append("longitude", longitude || "");
    formData.append("details", details || "");

    // Add default BOQ file path if no custom file is uploaded
    if (defaultBoqFile && !boqFile) {
      formData.append("default_boq_file", defaultBoqFile);
    }

    if (selectedHouseholds && selectedHouseholds.length > 0) {
      selectedHouseholds.forEach((householdId, index) => {
        formData.append(`households[${index}]`, householdId);
      });
    }
    if (boqFile) formData.append("boq_file", boqFile);
    if (masterPlanFile) formData.append("master_plan_file", masterPlanFile);
    try {
      let res;
      if (editingBoqId) {
        res = await putData(`proposed-features/boq/${editingBoqId}`, formData);
        toast.success("BOQ updated successfully");
      } else {
        res = await postData("proposed-features/boq", formData);
        toast.success("BOQ created successfully");
      }
      await fetchBoqs(locationData.village_id);
      clearBoqForm();
    } catch (error) {
      console.error("Error saving BOQ:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(`Failed to ${editingBoqId ? "update" : "create"} BOQ`);
      }
    }
  };

  const clearBoqForm = () => {
    setSelectedFeature("");
    setSelectedSubType(null);
    setSelectedCategory(null);
    setSelectedDesign(null);
    setConstructionType("");
    setCost("");
    setLatitude("");
    setLongitude("");
    clearBoqFileInput();
    setDefaultBoqFile(null);
    setDetails("");
    setSelectedHouseholds([]);
    setEditingBoqId(null);
    setErrors({});
    setProposedCode("");
    setSubTypes([]);
    setCategories([]);
    setDesigns([]);
    setSelectedMasterPlanFileName("");
  };

  const handleEditBoq = async (boq) => {
    try {
      if (households.length === 0) {
        await fetchHouseholds(selectedVillage);
      }

      setSelectedFeature(boq.feature_id);
      setSelectedSubType(boq.feature_sub_type_id);
      setSelectedCategory(boq.feature_category_id);
      setSelectedDesign(boq.feature_category_designs_id);
      setConstructionType(boq.construction_type || "");
      setCost(boq.cost || "");
      setLatitude(boq.latitude || "");
      setLongitude(boq.longitude || "");
      setDetails(boq.details || "");
      setUcName(boq.uc_name || "");
      let householdIds = [];

      if (
        boq.households &&
        Array.isArray(boq.households) &&
        boq.households.length > 0
      ) {
        householdIds = boq.households
          .filter((id) => id !== null && id !== undefined)
          .map((id) => parseInt(id))
          .filter((id) => !isNaN(id));
      } else if (
        boq.boqs_proposed_households &&
        Array.isArray(boq.boqs_proposed_households) &&
        boq.boqs_proposed_households.length > 0
      ) {
        householdIds = boq.boqs_proposed_households
          .map((hh) => parseInt(hh.household_information_id))
          .filter((id) => !isNaN(id) && id !== null && id !== undefined);
      } else if (
        boq.households_details &&
        Array.isArray(boq.households_details) &&
        boq.households_details.length > 0
      ) {
        householdIds = boq.households_details
          .map((hh) => parseInt(hh.household_information_id))
          .filter((id) => !isNaN(id) && id !== null && id !== undefined);
      }

      setSelectedHouseholds(householdIds);
      setEditingBoqId(boq.boqs_proposed_id);
      setProposedCode(boq.proposed_code || "");
      clearBoqFileInput();

      if (boq.feature_id) {
        const subTypesRes = await fetchData(
          `feature-subtypes-by-feature/${boq.feature_id}`,
          "GET"
        );
        const subTypesData = subTypesRes.data.map((s) => ({
          label: s.feature_sub_type,
          value: s.feature_sub_type_id,
        }));
        setSubTypes(subTypesData);

        if (boq.feature_sub_type_id) {
          const categoriesRes = await fetchData(
            `feature-categories-fetch-by-subtype/${boq.feature_sub_type_id}`,
            "GET"
          );
          const categoriesData = categoriesRes.data.map((c) => ({
            label: c.category_name,
            value: c.feature_category_id,
          }));
          setCategories(categoriesData);

          if (boq.feature_category_id) {
            const designsRes = await fetchData(
              `feature-designs/${boq.feature_category_id}`,
              "GET"
            );
            const designsData = designsRes.data.map((d) => ({
              label: d.design_name,
              value: d.feature_category_designs_id,
              boq_file: d.boq_file,
            }));
            setDesigns(designsData);

            const design = designsData.find(
              (d) => d.value === boq.feature_category_designs_id
            );

            if (design?.boq_file && !boq.boq_file_path) {
              setDefaultBoqFile(design.boq_file);
            } else {
              setDefaultBoqFile(null);
            }
          }
        }
      }

      // Small delay to ensure state updates are processed
      setTimeout(() => {
      }, 100);
    } catch (error) {
      console.error("Error preparing BOQ for edit:", error);
      toast.error("Failed to load BOQ for editing");
    }
  };

  const showDeleteConfirmation = (id) => {
    setSelectedDeleteId(id);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteBoq = async () => {
    if (!selectedDeleteId) return;

    const idToDelete = selectedDeleteId;

    // Close modal and clear state immediately
    setIsDeleteModalVisible(false);
    setSelectedDeleteId(null);

    try {
      await fetchData(`proposed-features/boq/${idToDelete}`, "DELETE");
      toast.success("BOQ deleted successfully");

      if (editingBoqId === idToDelete) {
        clearBoqForm();
      }

      await fetchBoqs(selectedVillage);
    } catch (error) {
      console.error("Error deleting BOQ:", error);
      toast.error("Failed to delete BOQ");
    }
  };

  useEffect(() => {
    // Close delete modal when switching tabs
    if (isDeleteModalVisible) {
      setIsDeleteModalVisible(false);
      setSelectedDeleteId(null);
    }
  }, [activeTab]);

  const getFeatureName = (boq) => {
    if (boq.feature && boq.feature.feature_name) {
      return boq.feature.feature_name;
    }
    const feature = features.find((f) => f.value === boq.feature_id);
    return feature ? feature.label : "Unknown Feature";
  };

  const getCategoryName = (boq) => {
    if (boq.FeaturesCategory && boq.FeaturesCategory.category_name) {
      return boq.FeaturesCategory.category_name;
    }
    if (boq.feature_category && boq.feature_category.category_name) {
      return boq.feature_category.category_name;
    }
    const category = categories.find(
      (c) => c.value === boq.feature_category_id
    );
    return category ? category.label : "Unknown Category";
  };

  const getVillageName = (boq) => {
    if (boq.village && boq.village.village_name) {
      return boq.village.village_name;
    }
    const village = villages.find((v) => v.value === boq.village_id);
    return village ? village.label : "Unknown Village";
  };

  const handleDesignChange = (designId) => {
    setSelectedDesign(designId);
    setErrors((prev) => ({ ...prev, design: "" }));

    if (designId && designs.length > 0) {
      const selectedDesignData = designs.find((d) => d.value == designId);

      if (selectedDesignData?.boq_file) {
        setDefaultBoqFile(selectedDesignData.boq_file);
      } else {
        setDefaultBoqFile(null);
      }
    } else {
      setDefaultBoqFile(null);
    }
  };

  const getBoqFileInfo = (boq) => {
    if (boq.boq_file_path) {
      return {
        type: "custom",
        path: boq.boq_file_path,
        label: "View BOQ",
      };
    }
    if (boq.default_boq_file) {
      return {
        type: "default",
        path: boq.default_boq_file,
        label: "View BOQ",
      };
    }
    return { type: "none", path: null, label: "No BOQ file" };
  };

  useEffect(() => {
    if (selectedVillage && selectedVillageData !== null) {
      const hasMasterPlan =
        selectedVillageData?.master_plan_file || masterPlanFile;
      setCanAccessBoqsTab(!!hasMasterPlan);
      setLocationErrors({});
    } else {
      setCanAccessBoqsTab(false);
    }
  }, [selectedVillage, selectedVillageData, masterPlanFile, isEditMode]);

  useEffect(() => {
    const initializeData = async () => {
      await fetchFeatures();
      if (villageId) {
        setIsEditMode(true);
        setSelectedVillage(parseInt(villageId));
        setActiveTab("BOQs");
        await loadLocationHierarchy(villageId);
        await fetchHouseholds(parseInt(villageId));
        await fetchBoqs(villageId);
      } else {
        setIsEditMode(false);
        await fetchDistricts();
      }
      setLoading(false);
    };
    initializeData();
  }, [villageId]);

  useEffect(() => {
    if (selectedDistrict && !isEditMode) {
      fetchTehsils(selectedDistrict);
      setSelectedTehsil(null);
    } else if (!selectedDistrict && !isEditMode) {
      setTehsils([]);
      setDehs([]);
      setSelectedTehsil(null);
    }
  }, [selectedDistrict, isEditMode]);

  // useEffect(() => {
  //   if (selectedTehsil && !isEditMode) {
  //     fetchDehs(selectedTehsil);
  //     setSelectedDeh(null);
  //   } else if (!selectedTehsil && !isEditMode) {
  //     setDehs([]);
  //     setSelectedDeh(null);
  //   }
  // }, [selectedTehsil, isEditMode]);

  // useEffect(() => {
  //   if (selectedTehsil && !isEditMode) {
  //     fetchUcs(selectedTehsil);
  //     setSelectedUc(null);
  //   } else if (!selectedTehsil && !isEditMode) {
  //     setUcs([]);
  //     setSelectedUc(null);
  //   }
  // }, [selectedTehsil, isEditMode]);
  useEffect(() => {
    if (selectedTehsil && !isEditMode) {
      fetchDehs(selectedTehsil);
      setSelectedDeh(null);
    } else if (!selectedTehsil && !isEditMode) {
      setDehs([]);
      setSelectedDeh(null);
    }
  }, [selectedTehsil, isEditMode]);

  useEffect(() => {
    if (selectedUc && !isEditMode) {
      fetchDehs(selectedUc);
      setSelectedDeh(null);
    } else if (!selectedUc && !isEditMode) {
      setDehs([]);
      setSelectedDeh(null);
    }
  }, [selectedUc, isEditMode]);

  useEffect(() => {
    if (selectedDeh && !isEditMode) {
      fetchVillages(selectedDeh);
      setSelectedVillage(null);
    } else if (!selectedDeh && !isEditMode) {
      setVillages([]);
      setSelectedVillage(null);
    }
  }, [selectedDeh, isEditMode]);

  useEffect(() => {
    if (selectedVillage && !isEditMode) {
      fetchVillageDetails(selectedVillage);
    } else if (!selectedVillage && !isEditMode) {
      setSelectedVillageData(null);
    }
  }, [selectedVillage, isEditMode]);

  useEffect(() => {
    if (selectedFeature) {
      fetchSubTypes(selectedFeature);
      if (!editingBoqId) {
        setSelectedSubType(null);
        setSelectedCategory(null);
        setSelectedDesign(null);
        setCategories([]);
        setDesigns([]);
        setDefaultBoqFile(null);
      }
    } else if (!selectedFeature) {
      setSubTypes([]);
      setSelectedSubType(null);
      setSelectedCategory(null);
      setSelectedDesign(null);
      setCategories([]);
      setDesigns([]);
      setDefaultBoqFile(null);
    }
  }, [selectedFeature]);

  useEffect(() => {
    if (selectedSubType) {
      fetchCategories(selectedSubType);
      if (!editingBoqId) {
        setSelectedCategory(null);
      }
    } else {
      setCategories([]);
      setSelectedCategory(null);
    }
  }, [selectedSubType]);

  useEffect(() => {
    if (selectedCategory) {
      fetchDesigns(selectedCategory);
      if (!editingBoqId) {
        setSelectedDesign(null);
        setDefaultBoqFile(null);
      }
    } else {
      setDesigns([]);
      setSelectedDesign(null);
      setDefaultBoqFile(null);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedDesign && designs.length > 0) {
      const selectedDesignData = designs.find((d) => d.value == selectedDesign);
      setDefaultBoqFile(selectedDesignData?.boq_file || null);
    } else {
      setDefaultBoqFile(null);
    }
  }, [selectedDesign, designs]);

  useEffect(() => {
    if (activeTab === "BOQs" && !editingBoqId) {
    }
  }, [activeTab, editingBoqId]);

  const availableHouseholds = useMemo(() => {
    if (!selectedCategory) return households;

    const usedIds = new Set();

    boqs.forEach((boq) => {
      if (boq.feature_category_id == selectedCategory) {
        if (editingBoqId && boq.boqs_proposed_id === editingBoqId) {
          return;
        }

        let householdIds = [];

        if (Array.isArray(boq.households) && boq.households.length > 0) {
          householdIds = boq.households;
        } else if (
          Array.isArray(boq.boqs_proposed_households) &&
          boq.boqs_proposed_households.length > 0
        ) {
          householdIds = boq.boqs_proposed_households.map(
            (hh) => hh.household_information_id
          );
        } else if (
          Array.isArray(boq.households_details) &&
          boq.households_details.length > 0
        ) {
          householdIds = boq.households_details.map(
            (hh) => hh.household_information_id
          );
        }

        householdIds.forEach((id) => {
          if (id !== null && id !== undefined) {
            const parsedId = parseInt(id);
            if (!isNaN(parsedId)) {
              usedIds.add(parsedId);
            }
          }
        });
      }
    });

    return households.filter((hh) => !usedIds.has(hh.value));
  }, [selectedCategory, editingBoqId, boqs, households]);

  const selectedHouseholdValues = useMemo(() => {
    if (!selectedHouseholds || selectedHouseholds.length === 0) {
      return [];
    }

    if (editingBoqId) {
      const allHouseholds = [...households];
      return allHouseholds.filter((hh) =>
        selectedHouseholds.includes(hh.value)
      );
    }

    return availableHouseholds.filter((hh) =>
      selectedHouseholds.includes(hh.value)
    );
  }, [selectedHouseholds, households, availableHouseholds, editingBoqId]);

  // Get options for React Select (available + currently selected)
  const householdOptions = useMemo(() => {
    if (editingBoqId) {
      // In edit mode, show available households plus currently selected ones
      const availableSet = new Set(availableHouseholds.map((h) => h.value));
      const selectedSet = new Set(selectedHouseholds);

      const allOptions = [...availableHouseholds];

      // Add currently selected households that might not be in available list
      households.forEach((hh) => {
        if (selectedSet.has(hh.value) && !availableSet.has(hh.value)) {
          allOptions.push(hh);
        }
      });

      return allOptions;
    }

    return availableHouseholds;
  }, [availableHouseholds, households, selectedHouseholds, editingBoqId]);

  const boqColumns = [
    {
      name: "Code",
      selector: (row) => row.proposed_code || "N/A",
      sortable: true,
    },
    {
      name: "Village",
      selector: (row) => getVillageName(row),
      sortable: true,
    },
    {
      name: "Feature Category",
      selector: (row) => getCategoryName(row),
      sortable: true,
    },
    {
      name: "Cost (PKR)",
      selector: (row) => (row.cost ? row.cost.toLocaleString() : "N/A"),
      sortable: true,
    },
    {
      name: "Households",
      cell: (row) => {
        const householdDetails = row.households_details || [];
        const householdIds = Array.isArray(row.households)
          ? row.households
          : row.boqs_proposed_households
          ? row.boqs_proposed_households.map(
              (hh) => hh.household_information_id
            )
          : [];
        const householdDisplay =
          householdDetails.length > 0
            ? householdDetails
                .map((hh) => `${hh.household_number} - ${hh.hh_head_name}`)
                .join(", ")
            : `${householdIds.length} household(s)`;
        return (
          <div title={householdDisplay} className="max-w-xs">
            <span className="text-sm">
              {householdDetails.length <= 2
                ? householdDisplay
                : `${householdDetails
                    .slice(0, 2)
                    .map((hh) => `${hh.household_number} - ${hh.hh_head_name}`)
                    .join(", ")} and ${householdDetails.length - 2} more`}
            </span>
          </div>
        );
      },
    },
    // {
    //   name: "Actions",
    //   cell: (row) => (
    //     <div className="flex gap-2">
    //       <button
    //         onClick={() => handleEditBoq(row)}
    //         className="hover:scale-110 transition-transform"
    //         title="Edit BOQ"
    //       >
    //         <i className="fa-solid fa-pen-to-square fa-lg"></i>
    //       </button>
    //       <button
    //         onClick={() => showDeleteConfirmation(row.boqs_proposed_id)}
    //         className="hover:scale-110 transition-transform"
    //         title="Delete BOQ"
    //       >
    //         <i
    //           className="fa fa-trash fa-lg text-primaryDark"
    //           aria-hidden="true"
    //         ></i>
    //       </button>
    //     </div>
    //   ),
    // },
  ];
  {
    if (
      permissions.includes("infrastructure-information-update") ||
      permissions.includes("infrastructure-information-delete")
    ) {
      boqColumns.push({
        name: "Actions",
        cell: (row) => (
          <div className="flex gap-2">
            {/* Edit Infrastructure */}
            {permissions.includes("infrastructure-information-update") && (
              <button
                onClick={() => handleEditBoq(row)}
                className="hover:scale-110 transition-transform"
                title="Edit Infrastructure"
              >
                <i className="fa-solid fa-pen-to-square fa-lg"></i>
              </button>
            )}

            {/* Delete Infrastructure */}
            {permissions.includes("infrastructure-information-delete") && (
              <button
                onClick={() => showDeleteConfirmation(row.boqs_proposed_id)}
                className="hover:scale-110 transition-transform"
                title="Delete BOQ"
              >
                <i
                  className="fa fa-trash fa-lg text-primaryDark"
                  aria-hidden="true"
                ></i>
              </button>
            )}
          </div>
        ),
      });
    }
  }

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "#fff",
        color: "black",
        fontWeight: "bold",
        fontSize: "0.88rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px strict #f3f3f3",
        textAlign: "center",
      },
    },
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          {isEditMode ? "Manage BOQs" : "Add New BOQs"}
        </h1>
      </div>

      <div className="mt-6">
        <div className="flex items-center rounded-t-lg rounded-tr-lg bg-[#fdf5ec] relative">
          <div className="flex gap-4 overflow-hidden scrollbar-hide">
            <button
              onClick={() => setActiveTab("Location")}
              className={`px-6 py-2 font-semibold whitespace-nowrap transition-colors ${
                activeTab === "Location"
                  ? "bg-[#caa316] text-white"
                  : "text-black hover:bg-gray-100"
              }`}
              style={{
                borderTopLeftRadius: "13px",
                borderTopRightRadius: "30px",
              }}
            >
              Location
            </button>
            <button
              onClick={() => setActiveTab("BOQs")}
              disabled={!canAccessBoqsTab}
              className={`px-6 py-2 font-semibold whitespace-nowrap transition-colors ${
                activeTab === "BOQs"
                  ? "bg-[#caa316] text-white"
                  : !canAccessBoqsTab
                  ? "text-gray-400 cursor-not-allowed opacity-50"
                  : "text-black hover:bg-gray-100"
              }`}
              style={{
                borderTopLeftRadius: "13px",
                borderTopRightRadius: "30px",
              }}
            >
              BOQs
            </button>
          </div>
        </div>

        <div className="bg-white rounded-b-lg rounded-br-lg shadow-md p-6">
          {activeTab === "Location" && (
            <div className="bg-white p-6 rounded shadow-md">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <ThinDropdownWithSearch
                    label="District"
                    options={districts}
                    value={selectedDistrict}
                    onChange={(value) => {
                      setSelectedDistrict(value);
                      setLocationErrors((prev) => ({ ...prev, district: "" }));
                    }}
                    disabled={isEditMode}
                  />
                  {locationErrors.district && (
                    <p className="text-red-500 text-sm mt-1">
                      {locationErrors.district}
                    </p>
                  )}
                </div>
                <div>
                  <ThinDropdownWithSearch
                    label="Tehsil"
                    options={tehsils}
                    value={selectedTehsil}
                    onChange={(value) => {
                      setSelectedTehsil(value);
                      setLocationErrors((prev) => ({ ...prev, tehsil: "" }));
                    }}
                    disabled={isEditMode}
                  />
                  {locationErrors.tehsil && (
                    <p className="text-red-500 text-sm mt-1">
                      {locationErrors.tehsil}
                    </p>
                  )}
                </div>
                <div>
                  {/* <ThinDropdownWithSearch
                    label="UC"
                    options={ucs}
                    value={selectedUc}
                    onChange={(value) => {
                      setSelectedUc(value);
                      setLocationErrors((prev) => ({ ...prev, uc: "" }));
                    }}
                    disabled={isEditMode}
                  />
                  {locationErrors.uc && (
                    <p className="text-red-500 text-sm mt-1">
                      {locationErrors.uc}
                    </p>
                  )} */}
                  <ThinTextField
                    label="UC Name"
                    // value={ucName}
                    value={ucName}
                    onChange={(e) => setUcName(e.target.value)}
                    isRequired={false}
                    // disabled={isEditMode}
                    error={locationErrors.uc}
                  />
                </div>
                <div>
                  <ThinDropdownWithSearch
                    label="Deh"
                    options={dehs}
                    value={selectedDeh}
                    onChange={(value) => {
                      setSelectedDeh(value);
                      setLocationErrors((prev) => ({ ...prev, deh: "" }));
                    }}
                    disabled={isEditMode}
                  />
                  {locationErrors.deh && (
                    <p className="text-red-500 text-sm mt-1">
                      {locationErrors.deh}
                    </p>
                  )}
                </div>
                <div>
                  <ThinDropdownWithSearch
                    label="Village"
                    options={villages}
                    value={selectedVillage}
                    onChange={(value) => {
                      setSelectedVillage(value);
                      setLocationErrors((prev) => ({ ...prev, village: "" }));
                    }}
                    disabled={isEditMode}
                  />
                  {locationErrors.village && (
                    <p className="text-red-500 text-sm mt-1">
                      {locationErrors.village}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  Master Plan File{" "}
                  {!isEditMode && !selectedVillageData?.master_plan_file && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                {selectedVillageData &&
                  selectedVillageData.master_plan_file && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-blue-700">
                          Current Master Plan:
                        </span>
                        <button
                          onClick={(e) =>
                            openPdfInNewTab(
                              selectedVillageData.master_plan_file,
                              e
                            )
                          }
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                        >
                          <i className="fa fa-external-link text-xs"></i>
                          View Current File
                        </button>
                      </div>
                    </div>
                  )}
                {selectedMasterPlanFileName && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-700">
                        Selected file: {selectedMasterPlanFileName}
                      </span>
                      <button
                        onClick={() => {
                          setMasterPlanFile(null);
                          setSelectedMasterPlanFileName("");
                          // Reset the file input
                          const fileInput = document.querySelector(
                            'input[type="file"][accept="image/*,application/pdf"]'
                          );
                          if (fileInput) fileInput.value = "";
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
                <input
                  type="file"
                  onChange={handleMasterPlanFileChange}
                  accept="image/*,application/pdf"
                  className="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
                />
                {locationErrors.masterPlanFile && (
                  <p className="text-red-500 text-sm mt-1">
                    {locationErrors.masterPlanFile}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {selectedVillageData && selectedVillageData.master_plan_file
                    ? "Upload a new file to replace the existing master plan"
                    : "Upload a master plan file (PDF or Image, max 1MB)"}
                </p>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleNext}
                  className={`mt-6 px-4 py-2 rounded-lg transition-colors ${
                    isEditMode
                      ? "bg-teal-600 text-white"
                      : "bg-teal-600 text-white"
                  } disabled:bg-gray-400`}
                >
                  {isEditMode ? "Update" : "Next"}
                </button>
              </div>
            </div>
          )}

          {activeTab === "BOQs" && (
            <div className="bg-white p-6 rounded shadow-md">
              <h2 className="text-xl font-semibold mb-4">
                {editingBoqId ? "Edit BOQ" : "Add New BOQ"}
              </h2>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <ThinDropdownWithSearch
                    label="Feature"
                    options={features}
                    value={selectedFeature}
                    onChange={(value) => {
                      setSelectedFeature(value);
                      setErrors((prev) => ({ ...prev, feature: "" }));
                    }}
                  />
                  {errors.feature && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.feature}
                    </p>
                  )}
                </div>
                <div>
                  <ThinDropdownWithSearch
                    label="Sub Type"
                    options={subTypes}
                    value={selectedSubType}
                    onChange={(value) => {
                      setSelectedSubType(value);
                      setErrors((prev) => ({ ...prev, subType: "" }));
                    }}
                  />
                  {errors.subType && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.subType}
                    </p>
                  )}
                </div>
                <div>
                  <ThinDropdownWithSearch
                    label="Category"
                    options={categories}
                    value={selectedCategory}
                    onChange={(value) => {
                      setSelectedCategory(value);
                      setErrors((prev) => ({ ...prev, category: "" }));
                    }}
                  />
                  {errors.category && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.category}
                    </p>
                  )}
                </div>
                <div>
                  <ThinDropdownWithSearch
                    label="Design"
                    options={designs}
                    value={selectedDesign}
                    onChange={(value) => {
                      handleDesignChange(value);
                    }}
                  />
                  {errors.design && (
                    <p className="text-red-500 text-sm mt-1">{errors.design}</p>
                  )}
                </div>
                <div>
                  <ThinDropdownWithSearch
                    label="Construction Type"
                    options={constructionOptions}
                    value={constructionType}
                    onChange={(value) => {
                      setConstructionType(value);
                      setErrors((prev) => ({ ...prev, constructionType: "" }));
                    }}
                  />
                  {errors.constructionType && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.constructionType}
                    </p>
                  )}
                </div>
                <div>
                  <ThinTextField
                    label="Cost (PKR)"
                    value={cost}
                    onChange={(e) => {
                      setCost(e.target.value);
                      setErrors((prev) => ({ ...prev, cost: "" }));
                    }}
                    type="number"
                  />
                  {errors.cost && (
                    <p className="text-red-500 text-sm mt-1">{errors.cost}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Households Served <span className="text-red-500">*</span>
                  </label>
                  <Select
                    isMulti
                    options={householdOptions}
                    value={selectedHouseholdValues}
                    onChange={(selectedOptions) => {
                      const selectedValues = selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : [];
                      setSelectedHouseholds(selectedValues);
                      setErrors((prev) => ({ ...prev, households: "" }));
                    }}
                    className="mt-1"
                    placeholder="Select households..."
                    noOptionsMessage={() =>
                      householdOptions.length === 0
                        ? "No households available for this village"
                        : "No options"
                    }
                  />
                  {errors.households && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.households}
                    </p>
                  )}
                  {households.length === 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      No households found. Please add households first.
                    </p>
                  )}
                </div>

                <div>
                  <ThinTextField
                    label="Latitude "
                    value={latitude}
                    onChange={(e) => {
                      setLatitude(e.target.value);
                      setErrors((prev) => ({ ...prev, latitude: "" }));
                    }}
                    type="number"
                    step="any"
                  />
                  {errors.latitude && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.latitude}
                    </p>
                  )}
                </div>
                <div>
                  <ThinTextField
                    label="Longitude"
                    value={longitude}
                    onChange={(e) => {
                      setLongitude(e.target.value);
                      setErrors((prev) => ({ ...prev, longitude: "" }));
                    }}
                    type="number"
                    step="any"
                  />
                  {errors.longitude && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.longitude}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    BOQ Document{" "}
                    {!defaultBoqFile && !editingBoqId && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>

                  {defaultBoqFile && !boqFile && !editingBoqId && (
                    <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-green-700">
                          Default BOQ from Design:
                        </span>
                        <button
                          onClick={(e) => openPdfInNewTab(defaultBoqFile, e)}
                          className="text-green-600 hover:text-green-800 text-sm flex items-center gap-1"
                        >
                          <i className="fa fa-external-link text-xs"></i>
                          View Default BOQ
                        </button>
                      </div>
                    </div>
                  )}

                  {editingBoqId &&
                    boqs.find((b) => b.boqs_proposed_id === editingBoqId)
                      ?.boq_file_path &&
                    !boqFile && (
                      <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-blue-700">
                            Current BOQ:
                          </span>
                          <button
                            onClick={(e) =>
                              openPdfInNewTab(
                                boqs.find(
                                  (b) => b.boqs_proposed_id === editingBoqId
                                )?.boq_file_path,
                                e
                              )
                            }
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                          >
                            <i className="fa fa-external-link text-xs"></i>
                            View Current BOQ
                          </button>
                        </div>
                      </div>
                    )}

                  {editingBoqId &&
                    !boqs.find((b) => b.boqs_proposed_id === editingBoqId)
                      ?.boq_file_path &&
                    defaultBoqFile &&
                    !boqFile && (
                      <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-green-700">
                            Default BOQ from Design:
                          </span>
                          <button
                            onClick={(e) => openPdfInNewTab(defaultBoqFile, e)}
                            className="text-green-600 hover:text-green-800 text-sm flex items-center gap-1"
                          >
                            <i className="fa fa-external-link text-xs"></i>
                            View Default BOQ
                          </button>
                        </div>
                      </div>
                    )}

                  {/* Show new file selected */}
                  {boqFile && (
                    <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <span className="text-sm text-yellow-700">
                        Selected file: {boqFile.name} (will replace existing
                        BOQ)
                      </span>
                    </div>
                  )}

                  <input
                    ref={boqFileInputRef}
                    type="file"
                    onChange={(e) => {
                      setBoqFile(e.target.files[0]);
                      setErrors((prev) => ({ ...prev, boqFile: "" }));
                    }}
                    accept="application/pdf"
                    className="block w-full border border-gray-300 rounded px-3 py-1"
                  />

                  <p className="text-xs text-gray-500 mt-1">
                    {editingBoqId
                      ? "Upload a PDF file to replace the existing BOQ (max 1MB)"
                      : defaultBoqFile
                      ? "A default BOQ is available from the selected design. Upload a PDF file to use a custom BOQ instead (max 1MB)"
                      : "Upload a PDF BOQ document (max 1MB)"}
                  </p>

                  {errors.boqFile && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.boqFile}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Details <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={details}
                    onChange={(e) => {
                      setDetails(e.target.value);
                      setErrors((prev) => ({ ...prev, details: "" }));
                    }}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    placeholder="Enter details..."
                  />
                  {errors.details && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.details}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-8 flex justify-end gap-4">
                {editingBoqId && (
                  <button
                    onClick={clearBoqForm}
                    className="bg-teal-600 text-white mb-4 px-4 py-2 rounded-lg hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                )}
                {/* <button
                  onClick={handleSubmitBoq}
                  className="bg-[#caa316] text-white mb-4 px-4 py-2 rounded-lg hover:bg-[#b8940f]"
                >
                  {editingBoqId ? "Update" : "Submit"}
                </button> */}

                {(editingBoqId
                  ? permissions.includes("infrastructure-information-update")
                  : permissions.includes(
                      "infrastructure-information-create"
                    )) && (
                  <button
                    onClick={handleSubmitBoq}
                    className="bg-[#caa316] text-white mb-4 px-4 py-2 rounded-lg hover:bg-[#b8940f]"
                  >
                    {editingBoqId ? "Update" : "Submit"}
                  </button>
                )}
              </div>

              {boqs.length === 0 ? (
                <p className="text-gray-500">No BOQs found</p>
              ) : (
                <DataTable
                  columns={boqColumns}
                  data={boqs}
                  customStyles={customStyles}
                  highlightOnHover
                />
              )}
            </div>
          )}
        </div>
      </div>
      <CustomConfirmModal
        isVisible={isDeleteModalVisible}
        onConfirm={handleDeleteBoq}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setSelectedDeleteId(null);
        }}
        message="Are you sure you want to delete this BOQ?"
      />
    </div>
  );
};

export default ManageBOQs;
